import { apiFetch } from './apiClient.js';

export type WidgetDesign = 'rounded' | 'square' | 'minimal';
export type WidgetBodyPattern = 'dots' | 'lines' | 'solid' | 'none';
export type WidgetLauncherPosition = 'left' | 'right';

export interface WidgetConfig {
  primaryColor: string;
  headerColor: string;
  backgroundColor: string;
  userBubbleColor: string;
  agentBubbleColor: string;
  textColor: string;
  mutedColor: string;
  fontFamily: string;
  design: WidgetDesign;
  borderRadius: number;
  bodyPattern: WidgetBodyPattern;
  launcherPosition: WidgetLauncherPosition;
  title: string;
  subtitle: string;
  profileImageUrl?: string;
  launcherIconUrl?: string;
}

export interface Widget {
  id: string;
  tenantId: string;
  name: string;
  isDefault: boolean;
  config: WidgetConfig;
  version: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface WidgetCreate {
  name: string;
  isDefault?: boolean;
  config: WidgetConfig;
}

export interface WidgetPatch {
  name?: string;
  isDefault?: boolean;
  config?: Partial<WidgetConfig>;
}

export const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;

interface ListWidgetsResponse {
  items: Widget[];
}

export async function listWidgets(
  tenantId: string,
  opts: { includeArchived?: boolean } = {},
): Promise<Widget[]> {
  const qs = opts.includeArchived ? '?includeArchived=true' : '';
  const res = await apiFetch<ListWidgetsResponse | Widget[]>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/widgets${qs}`,
  );
  return Array.isArray(res) ? res : res.items;
}

export async function getWidget(tenantId: string, widgetId: string): Promise<Widget> {
  return apiFetch<Widget>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/widgets/${encodeURIComponent(widgetId)}`,
  );
}

export async function createWidget(
  tenantId: string,
  body: WidgetCreate,
): Promise<Widget> {
  return apiFetch<Widget>(`/v1/tenants/${encodeURIComponent(tenantId)}/widgets`, {
    method: 'POST',
    body,
  });
}

export async function updateWidget(
  tenantId: string,
  widgetId: string,
  patch: WidgetPatch,
  ifMatchVersion: number,
): Promise<Widget> {
  return apiFetch<Widget>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/widgets/${encodeURIComponent(widgetId)}`,
    {
      method: 'PATCH',
      body: patch,
      headers: { 'If-Match': String(ifMatchVersion) },
    },
  );
}

export async function archiveWidget(tenantId: string, widgetId: string): Promise<Widget> {
  return apiFetch<Widget>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/widgets/${encodeURIComponent(widgetId)}/archive`,
    { method: 'POST' },
  );
}

export async function setDefaultWidget(
  tenantId: string,
  widgetId: string,
): Promise<Widget> {
  return apiFetch<Widget>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/widgets/${encodeURIComponent(widgetId)}/set-default`,
    { method: 'POST' },
  );
}

export const DEFAULT_WIDGET_CONFIG: WidgetConfig = {
  primaryColor: '#1f78ff',
  headerColor: '#0a2a66',
  backgroundColor: '#ffffff',
  userBubbleColor: '#1f78ff',
  agentBubbleColor: '#f1f5f9',
  textColor: '#0f172a',
  mutedColor: '#64748b',
  fontFamily: 'system-ui',
  design: 'rounded',
  borderRadius: 12,
  bodyPattern: 'none',
  launcherPosition: 'right',
  title: 'Hola',
  subtitle: '¿En qué podemos ayudarte?',
};
